import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { VendorProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vendor } from "assets/SideBarIcons/vendor.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { pickProfileEntityTitle } from "Helpers/pickProfileEntityTitle";
import { ChangeEvent, useEffect, useState } from "react";
import { GetVendorByIdResponse } from "subModule/src/services/vendor/interface";
import Loader from "layout/Loader";
import SupplierTabs from "./SupplierTabs";

interface FormData {
  [key: string]: any;
}

interface SupplierProps {
  setAddNew?: any;
  vendorsRefetch?: any;
}
export default function SupplierModal(props: SupplierProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "suppliersList",
    moduleHref: "/suppliers",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedSupplierNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phSupplierNameAr") : t("phSupplierNameEn");
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  useEffect(() => {
    if (params?.id && !props?.setAddNew) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    VendorProfileService.getVendorById(+(params as any)?.id)
      .then((res: GetVendorByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(pickProfileEntityTitle(res));
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id && !props?.setAddNew) {
      VendorProfileService.updateVendor(+params?.id, data)
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title || pickProfileEntityTitle(response));
            navigate(-1);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("vendor") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      VendorProfileService.createVendor(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("vendor") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew("");
              props.vendorsRefetch();
            } else navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          breadcrumbItems={breadcrumbItems}
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate(-1)}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew ? t("editVendor") : t("addVendor")
          }
          icon={<Vendor height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_VENDOR_CREATE")}
        />
      )}
      <div className={`${params?.id && !props?.setAddNew ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <SupplierTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id && !props?.setAddNew ? t("editVendor") : t("addVendor")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            padding={!props?.setAddNew}
            content={
              <Grid
                container
                component="form"
                id="form"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedSupplierNamePlaceholder(
                      primaryLanguage,
                    )}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedSupplierNamePlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("vendorCode", {
                      validate: {
                        required: (value) =>
                          !!String(value ?? "")?.trim() ||
                          t("This field is required."),
                      },
                    })}
                    label={t("supplierCode") + " *"}
                    placeholder={t("phSupplierCode")}
                    title={t("supplierCode")}
                    fullWidth
                    error={!!errors.vendorCode}
                    helperText={errors.vendorCode?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="text"
                    {...register("phoneNumber", {
                      validate: {
                        phoneLength: (value) =>
                          !value ||
                          (value?.length >= 5 && value?.length <= 17) ||
                          t("phoneLength"),
                      },
                    })}
                    label={t("phoneNumber")}
                    placeholder={t("phCompanyPhone")}
                    title={t("phoneNumber")}
                    fullWidth
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message as string}
                    InputLabelProps={{ shrink: true }}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                    ) => {
                      const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                      setValue("phoneNumber", cleaned);
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="email"
                    {...register("email", {
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: t("validEmail"),
                      },
                    })}
                    label={t("email")}
                    placeholder={t("phSupplierEmail")}
                    title={t("email")}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("address", {
                      maxLength: {
                        value: 500,
                        message: t("Address must be under 500 characters"),
                      },
                    })}
                    label={t("address")}
                    placeholder={t("phSupplierAddress")}
                    title={t("address")}
                    fullWidth
                    error={!!errors.address}
                    helperText={errors.address?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("taxNumber")}
                    label={t("taxNumber")}
                    placeholder={t("phSupplierTaxNumber")}
                    title={t("taxNumber")}
                    fullWidth
                    error={!!errors.taxNumber}
                    helperText={errors.taxNumber?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
